import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { conditionalRender } from '@repo/toolkit/conditional-render'
import { EuroIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import {
  CancellationReasonEnum,
  cancellationReasonEnumMapping
} from '../../../../lib/types/enums/enrollment/cancellation-reason.enum'
import { PaymentStatusEnum } from '../../../../lib/types/enums/enrollment/payment-status.enum'
import { CancelEnrollmentInput } from '../../../../lib/types/models/enrollment/enrollment-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { getSelectedEnumOption } from '../../../../lib/utils/functions/misc.functions'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import { createNumberSchema } from '../../../../lib/utils/validations/int-array-validation'
import { t } from '../../../../locale/utils'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledInput from '../../../elements/inputs/controlled-input'
import ControlledSelectInput from '../../../elements/inputs/controlled-select-input'
import ValidationButtons from '../../../elements/validation-buttons'

type Props = BaseFormProps<CancelEnrollmentInput> & {
  className?: string
  paymentStatus: PaymentStatusEnum
  isConfirmed: boolean
}

// The cancellation fee invoice of a confirmed enrollment prints the retained amount, so the admin
// must type it; the amount transmitted to the insurer is only known when there is a claim
const getFormSchema = (isConfirmed: boolean): ZodType<CancelEnrollmentInput> =>
  z
    .object({
      reason: createEnumSchema({
        fieldName: "Motif de l'annulation",
        enumType: CancellationReasonEnum
      }),
      refundAmount: createNumberSchema('Montant à rembourser', {
        isPositive: true,
        isInteger: false,
        nullish: true
      }),
      retainedAmount: createNumberSchema('Total retenu', {
        isAtLeastZero: true,
        isInteger: false,
        nullish: true
      }),
      insuranceAmount: createNumberSchema("Total transmis à l'assurance", {
        isAtLeastZero: true,
        isInteger: false,
        nullish: true
      })
    })
    .refine(
      // checked here rather than with a required number: a cleared input is null, which the
      // number coercion would silently turn into 0
      (data) =>
        !isConfirmed || (data.retainedAmount !== null && data.retainedAmount !== undefined),
      {
        message: t('errors.number.required', { fieldName: 'Total retenu' }),
        path: ['retainedAmount']
      }
    )

const confirmedRequestCancellationReasons: CancellationReasonEnum[] = [
  CancellationReasonEnum.CANCELLATION_BY_LEC_FOREIGN_PROD_CONSTRAINT,
  CancellationReasonEnum.CANCELLATION_BY_LEC_LACK_OF_STAFF_FOREIGN,
  CancellationReasonEnum.CANCELLATION_BY_LEC_TRIP_PROD_CONSTRAINT,
  CancellationReasonEnum.CANCELLATION_BY_LEC_LACK_OF_STAFF_TRIP,
  CancellationReasonEnum.CANCELLATION_BY_CLIENT_HEALTH,
  CancellationReasonEnum.CANCELLATION_BY_CLIENT_PAPERWORK_PERSONAL_REASON
]

const notConfirmedRequestCancellationReasons: CancellationReasonEnum[] = [
  CancellationReasonEnum.CANCELLATION_BY_LEC_COMPLETE_STAY,
  CancellationReasonEnum.CANCELLATION_BY_LEC_COMPLETE_TRIP,
  CancellationReasonEnum.CANCELLATION_BY_LEC_YOUTH_PROFILE_INCOMPATIBLE_WITH_STAY,
  CancellationReasonEnum.CANCELLATION_BY_LEC_CONTRADICTORY_PROCEDURE,
  CancellationReasonEnum.CANCELLATION_BY_LEC_TEST,
  CancellationReasonEnum.CANCELLATION_BY_CLIENT_CHANGED_MIND_NO_FOLLOW_UP
]

// Money is only captured once the enrollment is confirmed, before that there is only a preauthorization
const refundablePaymentStatus: PaymentStatusEnum[] = [
  PaymentStatusEnum.DEPOSIT_PAID,
  PaymentStatusEnum.FULLY_PAID,
  PaymentStatusEnum.PARTIALLY_PAID
]

export function CancelEnrollmentForm({
  defaultValues,
  paymentStatus,
  isConfirmed,
  className,
  ...validationButtonProps
}: Props) {
  const form = useForm<CancelEnrollmentInput>({
    resolver: zodResolver(getFormSchema(isConfirmed)),
    defaultValues,
    mode: FORM_MODE
  })

  const cancellationReasons = isConfirmed
    ? confirmedRequestCancellationReasons
    : notConfirmedRequestCancellationReasons
  const canRefund = isConfirmed && refundablePaymentStatus.includes(paymentStatus)
  const submitAction = getSubmitAction(validationButtonProps.buttonsConfig)

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit((data) =>
          submitAction({
            ...data,
            refundAmount: canRefund ? data.refundAmount : undefined,
            retainedAmount: isConfirmed ? data.retainedAmount : undefined,
            insuranceAmount: isConfirmed ? (data.insuranceAmount ?? undefined) : undefined
          })
        )}
      >
        <div className={cn('flex flex-col gap-4', className)}>
          <div className="flex flex-col gap-2">
            <Typography variant="label-semibold">Motif de l'annulation</Typography>
            <FormField
              control={form.control}
              name={'reason'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledSelectInput
                      {...field}
                      searchable
                      dropDownPosition="above"
                      label={"Motif de l'annulation"}
                      options={cancellationReasons.map((status) => ({
                        label: cancellationReasonEnumMapping[status],
                        value: status
                      }))}
                      selectedOption={conditionalRender(
                        !!field?.value,
                        getSelectedEnumOption(field.value, cancellationReasonEnumMapping)
                      )}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          {canRefund && (
            <FormField
              control={form.control}
              name={'refundAmount'}
              render={({ field }) => (
                <FormItem className={'flex-1'}>
                  <FormControl>
                    <ControlledInput
                      {...field}
                      type="number"
                      icon={EuroIcon}
                      label={'Montant à rembourser (€)'}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          )}

          {isConfirmed && (
            <>
              <FormField
                control={form.control}
                name={'retainedAmount'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledInput
                        {...field}
                        type="number"
                        icon={EuroIcon}
                        label={'Total retenu selon les Conditions Générales de Vente LEC (€)'}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={'insuranceAmount'}
                render={({ field }) => (
                  <FormItem className={'flex-1'}>
                    <FormControl>
                      <ControlledInput
                        {...field}
                        type="number"
                        icon={EuroIcon}
                        label={"Total transmis à l'assurance (€)"}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </>
          )}
        </div>
        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}
